/**
 * Selection Summary Dialog — shown before OvaDrive update.
 * Displays a per-plate breakdown of selected temp + live embryos
 * so the user can confirm before proceeding.
 */
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
} from '@/services/freeze';

type SelectionSummaryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceed: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  fertilizations: FertilizationListFreezeResponse[];
  embryosByFert: Map<string, FertAndEmbryosResponse>;
  selectedFertIds: string[];
  selectedTempIds: string[];
  selectedLiveIds: string[];
};

type PlateSummary = {
  plateNumber: number;
  tempCount: number;
  liveCount: number;
};

export function SelectionSummaryDialog({
  open,
  onOpenChange,
  onProceed,
  onCancel,
  isLoading,
  fertilizations,
  embryosByFert,
  selectedFertIds,
  selectedTempIds,
  selectedLiveIds,
}: SelectionSummaryDialogProps) {
  const summary = useMemo(() => {
    if (!open) return null;

    const plateMap = new Map<
      number,
      { tempCount: number; liveCount: number }
    >();

    for (const fertId of selectedFertIds) {
      const fert = fertilizations.find((f) => f.fertId === fertId);
      if (!fert) continue;
      const plateNumber = fert.plateNumber ?? 0;
      const embryoData = embryosByFert.get(fertId);
      if (!embryoData) continue;

      const tempCount = (embryoData.tempEmbryos ?? []).filter((e) =>
        selectedTempIds.includes(e.embryoFreezeEntryId ?? ''),
      ).length;

      const liveCount = (embryoData.liveEmbryos ?? []).filter((e) =>
        selectedLiveIds.includes(e.id ?? ''),
      ).length;

      if (tempCount > 0 || liveCount > 0) {
        const existing = plateMap.get(plateNumber) ?? {
          tempCount: 0,
          liveCount: 0,
        };
        plateMap.set(plateNumber, {
          tempCount: existing.tempCount + tempCount,
          liveCount: existing.liveCount + liveCount,
        });
      }
    }

    const items: PlateSummary[] = Array.from(plateMap.entries())
      .map(([plateNumber, counts]) => ({ plateNumber, ...counts }))
      .sort((a, b) => a.plateNumber - b.plateNumber);

    return {
      items,
      totalTemp: selectedTempIds.length,
      totalLive: selectedLiveIds.length,
      totalFerts: selectedFertIds.length,
    };
  }, [
    open,
    fertilizations,
    embryosByFert,
    selectedFertIds,
    selectedTempIds,
    selectedLiveIds,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm Update</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-sm font-medium">
            Are you sure you want to update OvaDrive with the following
            selection?
          </p>

          {summary && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-3 flex gap-4 text-sm font-semibold">
                <span>
                  Total: {summary.totalTemp} Temp Embryos, {summary.totalLive}{' '}
                  Live Embryos
                </span>
              </div>

              <div className="max-h-[300px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 font-medium">Plate</th>
                      <th className="py-1.5 text-center font-medium">
                        Temp Embryos
                      </th>
                      <th className="py-1.5 text-center font-medium">
                        Live Embryos
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.items.map((item) => (
                      <tr
                        key={item.plateNumber}
                        className="border-b border-gray-100 dark:border-gray-700"
                      >
                        <td className="py-1.5">Plate {item.plateNumber}</td>
                        <td className="py-1.5 text-center">
                          {item.tempCount > 0 ? (
                            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                              {item.tempCount}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-1.5 text-center">
                          {item.liveCount > 0 ? (
                            <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900 dark:text-green-300">
                              {item.liveCount}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onProceed} disabled={isLoading}>
            {isLoading ? (
              <i className="pi pi-spin pi-spinner mr-1.5 text-xs" />
            ) : (
              <i className="pi pi-check mr-1.5 text-xs" />
            )}
            {isLoading ? 'Updating…' : 'Proceed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
